import React, { useEffect, useMemo, useRef, useState } from "react";
import ConfirmModal from "../../common/ConfirmModal";
import QmsVaultReviewCycleTimeline from "./QmsVaultReviewCycleTimeline";
import { QmsVaultPersonCard } from "./QmsVaultPersonCard";
import { hasVaultPeopleCycleTimeline } from "./qmsVaultReviewCycleUtils";
import {
  canRemoveVaultApprover,
  canRemoveVaultReviewer,
  getAssignedVaultPeopleIds,
  getVaultApproverPicker,
  getVaultReviewerPicker,
  getVaultSavedApprovers,
  getVaultSavedReviewers,
  getUnsentVaultPeopleIds,
} from "./qmsVaultWorkspaceUtils";

const PEOPLE_CONFIG = {
  reviewers: {
    title: "Reviewers",
    pickerPlaceholder: "Add reviewers…",
    pickerIdField: "ReviewerId",
    pickerNameField: "ReviewerName",
    savedRowsFn: getVaultSavedReviewers,
    pickerFn: getVaultReviewerPicker,
    savedIdField: "ReviewerId",
    savedNameField: "ReviewerName",
    completedField: "ReveiwCompletedDateTime",
    sentDateField: "ReviewSentDate",
    commentField: "ReviewerComment",
    commentDateField: "ReviewedDate",
    commentLabel: "Last reviewer comment",
    actionDateField: "ReviewedDate",
    statusField: "ReviewStatus",
    completedAtField: "ReveiwCompletedDateTime",
    showActionDate: false,
    canRemove: canRemoveVaultReviewer,
    saveLabel: "Save",
    sendLabel: "Send for review",
    completeLabel: "Review complete",
    completeTitle: "Review complete",
    completeMessage: "Are you sure you want to mark this review cycle as complete?",
    saveAction: "SaveReviewer",
    sendAction: "SaveSendReviewer",
    removeTitle: "Remove reviewer",
    removeMessage:
      "Are you sure you want to remove this reviewer from the current review cycle? This action cannot be undone.",
    sentLabel: "Sent for review",
    actionLabel: "Reviewed on",
    completedLabel: "Review completed",
    pendingCompletedLabel: "In review",
    actionedLabel: "Reviewed",
    icon: "fas fa-user-check",
  },
  approvers: {
    title: "Approvers",
    pickerPlaceholder: "Add approvers…",
    pickerIdField: "ApproverId",
    pickerNameField: "ApproverName",
    savedRowsFn: getVaultSavedApprovers,
    pickerFn: getVaultApproverPicker,
    savedIdField: "ApproverId",
    savedNameField: "ApproverName",
    completedField: "ApprovalCompletedDateTime",
    sentDateField: "ApproveSentDate",
    commentField: "ApproverComment",
    commentDateField: "ApprovedDate",
    commentLabel: "Last approver comment",
    actionDateField: "ApprovedDate",
    statusField: "ApproveStatus",
    completedAtField: "ApprovalCompletedDateTime",
    showActionDate: false,
    hideIcon: true,
    canRemove: canRemoveVaultApprover,
    saveLabel: "Save",
    sendLabel: "Send for approval",
    completeLabel: "Approval complete",
    completeTitle: "Approval complete",
    completeMessage: "Are you sure you want to mark this approval cycle as complete?",
    saveAction: "SaveApprover",
    sendAction: "SaveSendApprover",
    removeTitle: "Remove approver",
    removeMessage:
      "Are you sure you want to remove this approver from the current approval cycle? This action cannot be undone.",
    sentLabel: "Sent for approval",
    actionLabel: "Approved on",
    completedLabel: "Approval completed",
    pendingCompletedLabel: "In approval",
    actionedLabel: "Responded",
    icon: "fas fa-user-shield",
  },
};

function CompactPeoplePicker({
  mode,
  config,
  rows,
  selectedIds,
  saving,
  onToggle,
  onClear,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      String(row[config.pickerNameField] || "")
        .toLowerCase()
        .includes(q),
    );
  }, [config.pickerNameField, rows, search]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const triggerLabel = useMemo(() => {
    if (rows.length === 0) return `All ${mode} assigned`;
    if (!selectedIds.length) return config.pickerPlaceholder;
    if (selectedIds.length === 1) {
      const row = rows.find((item) => String(item[config.pickerIdField]) === selectedIds[0]);
      return row?.[config.pickerNameField] || "1 selected";
    }
    return `${selectedIds.length} selected`;
  }, [config, mode, rows, selectedIds]);

  return (
    <div className="qms-vault-people__picker" ref={rootRef}>
      <button
        type="button"
        className="qms-vault-people__picker-trigger"
        disabled={saving || rows.length === 0}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="qms-vault-people__picker-trigger-text">{triggerLabel}</span>
        <i className={`fas fa-chevron-${open ? "up" : "down"}`} aria-hidden="true" />
      </button>

      {open && rows.length > 0 ? (
        <div className="qms-vault-people__picker-menu" role="listbox">
          <input
            type="search"
            className="qms-vault-people__picker-search"
            placeholder="Search…"
            value={search}
            autoFocus
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="qms-vault-people__picker-options">
            {filteredRows.length === 0 ? (
              <p className="qms-vault-people__picker-none">No matches.</p>
            ) : (
              filteredRows.map((row) => {
                const id = String(row[config.pickerIdField] || "");
                const name = row[config.pickerNameField] || id;
                const checked = selectedIds.includes(id);
                return (
                  <label key={id} className="qms-vault-people__picker-option">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={saving}
                      onChange={() => onToggle(id)}
                    />
                    <span>{name}</span>
                  </label>
                );
              })
            )}
          </div>
          {selectedIds.length > 0 ? (
            <button
              type="button"
              className="qms-vault-people__picker-clear"
              onClick={onClear}
            >
              Clear selection
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function QmsVaultPeoplePanel({
  mode = "reviewers",
  workspaceResponse,
  workflow,
  saving = false,
  readOnly = false,
  onSave,
  onRemove,
  onComplete,
}) {
  const config = PEOPLE_CONFIG[mode];
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: null,
    id: null,
  });
  const [pendingAction, setPendingAction] = useState(null);
  const actionLockRef = useRef(false);

  const isBusy = saving || Boolean(pendingAction);

  const pickerRows = useMemo(
    () => config.pickerFn(workspaceResponse),
    [config, workspaceResponse],
  );
  const savedRows = useMemo(
    () => config.savedRowsFn(workspaceResponse),
    [config, workspaceResponse],
  );
  const assignedIds = useMemo(
    () =>
      getAssignedVaultPeopleIds(
        savedRows,
        config.savedIdField,
        config.completedField,
      ),
    [savedRows, config.savedIdField, config.completedField],
  );

  const availablePickerRows = useMemo(
    () =>
      pickerRows.filter(
        (row) => !assignedIds.has(String(row[config.pickerIdField] || "")),
      ),
    [pickerRows, assignedIds, config.pickerIdField],
  );

  const unsentIds = useMemo(
    () =>
      getUnsentVaultPeopleIds(
        savedRows,
        config.savedIdField,
        config.statusField,
        config.sentDateField,
      ),
    [config.savedIdField, config.sentDateField, config.statusField, savedRows],
  );

  useEffect(() => {
    if (!saving) {
      setPendingAction(null);
      actionLockRef.current = false;
    }
  }, [saving]);

  const runLockedAction = async (actionType, callback) => {
    if (actionLockRef.current || saving) return false;
    actionLockRef.current = true;
    setPendingAction(actionType);
    try {
      await callback();
    } finally {
      setPendingAction(null);
      actionLockRef.current = false;
    }
    return true;
  };

  const toggleSelected = (id) => {
    const key = String(id);
    setSelectedIds((prev) =>
      prev.includes(key) ? prev.filter((value) => value !== key) : [...prev, key],
    );
  };

  const handleSave = () => {
    if (!selectedIds.length) return;
    const ids = selectedIds.join(",");
    setSelectedIds([]);
    runLockedAction("save", () => onSave?.(config.saveAction, ids));
  };

  const handleSend = () => {
    const idsToSend = selectedIds.length > 0 ? selectedIds : unsentIds;
    if (!idsToSend.length) return;
    setSelectedIds([]);
    runLockedAction("send", () => onSave?.(config.sendAction, idsToSend.join(",")));
  };

  const handleConfirmRemove = () => {
    if (!confirmModal.id) return;
    const id = confirmModal.id;
    setConfirmModal({ open: false, type: null, id: null });
    runLockedAction("remove", () => onRemove?.(id));
  };

  const handleConfirmComplete = () => {
    setConfirmModal({ open: false, type: null, id: null });
    runLockedAction("complete", () => onComplete?.());
  };

  const canSave = readOnly
    ? false
    : mode === "reviewers"
      ? workflow?.canSaveReviewers
      : workflow?.canSaveApprovers;
  const canSend = readOnly
    ? false
    : mode === "reviewers"
      ? workflow?.canSendReviewers
      : workflow?.canSendApprovers;
  const canComplete = readOnly
    ? false
    : mode === "reviewers"
      ? workflow?.canCompleteReview
      : workflow?.canCompleteApproval;

  const sendCount = selectedIds.length > 0 ? selectedIds.length : unsentIds.length;
  const sendDisabled = isBusy || sendCount === 0;
  const showPeopleTimeline = hasVaultPeopleCycleTimeline(workspaceResponse, mode);
  // When timeline is shown, people live only inside cycles — no duplicate list below.
  const listRows = useMemo(
    () => (showPeopleTimeline ? [] : savedRows),
    [savedRows, showPeopleTimeline],
  );

  return (
    <div
      className={`qms-doc-workspace__panel qms-vault-people qms-vault-people--compact qms-vault-audit${
        isBusy ? " is-busy" : ""
      }`}
    >
      <div className="qms-vault-people__bar">
        <div className="qms-vault-people__bar-title">
          <h6>{config.title}</h6>
        </div>

        {canSave ? (
          <div className="qms-vault-people__bar-actions">
            <CompactPeoplePicker
              mode={mode}
              config={config}
              rows={availablePickerRows}
              selectedIds={selectedIds}
              saving={isBusy}
              onToggle={toggleSelected}
              onClear={() => setSelectedIds([])}
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={isBusy || !selectedIds.length}
              onClick={handleSave}
              title="Save selected without sending"
            >
              {pendingAction === "save" ? (
                <>
                  <i className="fas fa-spinner fa-spin me-1" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                config.saveLabel
              )}
            </button>
            {canSend ? (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={sendDisabled}
                onClick={handleSend}
                title={
                  unsentIds.length > 0 && !selectedIds.length
                    ? `Send ${unsentIds.length} saved ${mode.slice(0, -1)}(s)`
                    : config.sendLabel
                }
              >
                {pendingAction === "send" ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-1" aria-hidden="true" />
                    Sending…
                  </>
                ) : (
                  <>
                    {config.sendLabel}
                    {sendCount > 0 ? ` (${sendCount})` : ""}
                  </>
                )}
              </button>
            ) : null}
          </div>
        ) : null}

        {canComplete ? (
          <button
            type="button"
            className="btn btn-sm btn-success"
            disabled={isBusy}
            onClick={() => {
              if (isBusy) return;
              setConfirmModal({ open: true, type: "complete", id: null });
            }}
          >
            {pendingAction === "complete" ? (
              <>
                <i className="fas fa-spinner fa-spin me-1" aria-hidden="true" />
                Updating…
              </>
            ) : (
              config.completeLabel
            )}
          </button>
        ) : null}
      </div>

      <div className="qms-vault-people__list-wrap">
        {showPeopleTimeline ? (
          <QmsVaultReviewCycleTimeline
            mode={mode}
            workspaceResponse={workspaceResponse}
            readOnly={readOnly}
            canSave={canSave}
            isBusy={isBusy}
            removingId={pendingAction === "remove" ? confirmModal.id : null}
            onRemoveRequest={(personId) =>
              setConfirmModal({ open: true, type: "remove", id: personId })
            }
          />
        ) : null}

        {showPeopleTimeline && listRows.length === 0 ? null : (
          <div className="qms-vault-audit__list-scroll">
            {listRows.length === 0 ? (
              <div className="qms-vault-audit__empty">No {mode} assigned yet.</div>
            ) : (
              <div className="qms-vault-audit__list">
                {listRows.map((row) => {
                  const id = String(row[config.savedIdField] || "");
                  return (
                    <QmsVaultPersonCard
                      key={id}
                      row={row}
                      config={config}
                      readOnly={readOnly}
                      canSave={canSave}
                      canRemove={config.canRemove(row)}
                      isBusy={isBusy}
                      isRemoving={pendingAction === "remove" && confirmModal.id === id}
                      onRemoveRequest={(personId) =>
                        setConfirmModal({
                          open: true,
                          type: "remove",
                          id: personId,
                        })
                      }
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        title={
          confirmModal.type === "complete"
            ? config.completeTitle
            : config.removeTitle
        }
        message={
          confirmModal.type === "complete"
            ? config.completeMessage
            : config.removeMessage
        }
        confirmText={
          confirmModal.type === "complete"
            ? "Yes"
            : pendingAction === "remove"
              ? "Removing…"
              : "Remove"
        }
        confirmButtonClass={
          confirmModal.type === "complete" ? "btn-success" : "btn-danger"
        }
        icon={
          confirmModal.type === "complete"
            ? "fas fa-check-circle text-success"
            : "fas fa-exclamation-triangle text-danger"
        }
        onClose={() => {
          if (isBusy) return;
          setConfirmModal({ open: false, type: null, id: null });
        }}
        onConfirm={
          confirmModal.type === "complete"
            ? handleConfirmComplete
            : handleConfirmRemove
        }
      />
    </div>
  );
}

export default QmsVaultPeoplePanel;
